/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { authenticatedFetch, createQueryOptions, createMutationFn } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import type {
  ChatSessionResponseData,
  ChatSessionsResponseData,
  ChatMessageResponseData,
  ChatMessagesResponseData,
  ChatSessionParticipantResponseData,
  ChatSessionParticipantsResponseData,
  ChatSessionWithMessagesResponseData,
  ChatSessionWithParticipantsResponseData,
  CreateChatSessionResponseData,
  UpdateChatSessionResponseData,
  CreateChatMessageResponseData,
  UpdateChatMessageResponseData,
  DeleteChatSessionResponseData,
  DeleteChatMessageResponseData
} from '@shared/types/responses/chat';
import type {
  CreateChatSessionRequestData,
  UpdateChatSessionRequestData,
  CreateChatMessageRequestData,
  UpdateChatMessageRequestData,
  CreateChatSessionParticipantRequestData,
  UpdateChatSessionParticipantRequestData,
  GetChatSessionsRequestData,
  GetChatMessagesRequestData
} from '@shared/types/requests/chat';

// Chat Session API Functions

/**
 * Get chat sessions with optional filtering
 */
export async function getChatSessions(params?: GetChatSessionsRequestData): Promise<ChatSessionsResponseData> {
  const queryParams = new URLSearchParams();
  if (params?.userId) queryParams.append('userId', params.userId.toString());
  if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

  const url = `/api/chat/sessions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await authenticatedFetch(url);
  return handleApiResponse<ChatSessionsResponseData>(response);
}

/**
 * Get a specific chat session by ID
 */
export async function getChatSession(sessionId: number): Promise<ChatSessionResponseData> {
  const response = await authenticatedFetch(`/api/chat/sessions/${sessionId}`);
  return handleApiResponse<ChatSessionResponseData>(response);
}

/**
 * Get a chat session by session key
 */
export async function getChatSessionByKey(sessionKey: string): Promise<ChatSessionResponseData> {
  const response = await authenticatedFetch(`/api/chat/sessions/by-key/${encodeURIComponent(sessionKey)}`);
  return handleApiResponse<ChatSessionResponseData>(response);
}

/**
 * Get a chat session with all messages
 */
export async function getChatSessionWithMessages(sessionId: number): Promise<ChatSessionWithMessagesResponseData> {
  const response = await authenticatedFetch(`/api/chat/sessions/${sessionId}/with-messages`);
  return handleApiResponse<ChatSessionWithMessagesResponseData>(response);
}

/**
 * Create a new chat session
 */
export async function createChatSession(sessionData: CreateChatSessionRequestData): Promise<CreateChatSessionResponseData> {
  const response = await authenticatedFetch('/api/chat/sessions', {
    method: 'POST',
    body: sessionData
  });
  return handleApiResponse<CreateChatSessionResponseData>(response);
}

/**
 * Update a chat session
 */
export async function updateChatSession(sessionId: number, updates: UpdateChatSessionRequestData): Promise<UpdateChatSessionResponseData> {
  const response = await authenticatedFetch(`/api/chat/sessions/${sessionId}`, {
    method: 'PUT',
    body: updates
  });
  return handleApiResponse<UpdateChatSessionResponseData>(response);
}

/**
 * Delete a chat session
 */
export async function deleteChatSession(sessionId: number): Promise<DeleteChatSessionResponseData> {
  const response = await authenticatedFetch(`/api/chat/sessions/${sessionId}`, {
    method: 'DELETE'
  });
  return handleApiResponse<DeleteChatSessionResponseData>(response);
}

// Chat Message API Functions

/**
 * Get messages for a chat session
 */
export async function getChatMessages(sessionId: number, params?: Omit<GetChatMessagesRequestData, 'sessionId'>): Promise<ChatMessagesResponseData> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());
  if (params?.includeDeleted) queryParams.append('includeDeleted', params.includeDeleted.toString());

  const url = `/api/chat/sessions/${sessionId}/messages${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await authenticatedFetch(url);
  return handleApiResponse<ChatMessagesResponseData>(response);
}

/**
 * Get a specific chat message
 */
export async function getChatMessage(messageId: number): Promise<ChatMessageResponseData> {
  const response = await authenticatedFetch(`/api/chat/messages/${messageId}`);
  return handleApiResponse<ChatMessageResponseData>(response);
}

/**
 * Create a new chat message
 */
export async function createChatMessage(messageData: CreateChatMessageRequestData): Promise<CreateChatMessageResponseData> {
  const response = await authenticatedFetch('/api/chat/messages', {
    method: 'POST',
    body: messageData
  });
  return handleApiResponse<CreateChatMessageResponseData>(response);
}

/**
 * Update a chat message
 */
export async function updateChatMessage(messageId: number, updates: UpdateChatMessageRequestData): Promise<UpdateChatMessageResponseData> {
  const response = await authenticatedFetch(`/api/chat/messages/${messageId}`, {
    method: 'PUT',
    body: updates
  });
  return handleApiResponse<UpdateChatMessageResponseData>(response);
}

/**
 * Delete a chat message (soft delete)
 */
export async function deleteChatMessage(messageId: number): Promise<DeleteChatMessageResponseData> {
  const response = await authenticatedFetch(`/api/chat/messages/${messageId}`, {
    method: 'DELETE'
  });
  return handleApiResponse<DeleteChatMessageResponseData>(response);
}

// Chat Session Participants API Functions

/**
 * Get participants for a chat session
 */
export async function getChatSessionParticipants(sessionId: number): Promise<ChatSessionParticipantsResponseData> {
  const response = await authenticatedFetch(`/api/chat/sessions/${sessionId}/participants`);
  return handleApiResponse<ChatSessionParticipantsResponseData>(response);
}

/**
 * Add a participant to a chat session
 */
export async function createChatSessionParticipant(participantData: CreateChatSessionParticipantRequestData): Promise<ChatSessionParticipantResponseData> {
  const response = await authenticatedFetch('/api/chat/participants', {
    method: 'POST',
    body: participantData
  });
  return handleApiResponse<ChatSessionParticipantResponseData>(response);
}

/**
 * Update a session participant
 */
export async function updateChatSessionParticipant(
  sessionId: number, 
  agentId: number, 
  updates: UpdateChatSessionParticipantRequestData
): Promise<ChatSessionParticipantResponseData> {
  const response = await authenticatedFetch(`/api/chat/sessions/${sessionId}/participants/${agentId}`, {
    method: 'PUT',
    body: updates
  });
  return handleApiResponse<ChatSessionParticipantResponseData>(response);
}

/**
 * Remove a participant from a chat session
 */
export async function deleteChatSessionParticipant(sessionId: number, agentId: number): Promise<{ success: boolean; sessionId: number; agentId: number }> {
  const response = await authenticatedFetch(`/api/chat/sessions/${sessionId}/participants/${agentId}`, {
    method: 'DELETE'
  });
  return handleApiResponse<{ success: boolean; sessionId: number; agentId: number }>(response);
}

// React Query Integration Helpers

/**
 * Create query options for getting chat sessions
 */
export function createGetChatSessionsQueryOptions(params?: GetChatSessionsRequestData) {
  const queryParams = new URLSearchParams();
  if (params?.userId) queryParams.append('userId', params.userId.toString());
  if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());
  
  const endpoint = `/api/chat/sessions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return {
    ...createQueryOptions<ChatSessionsResponseData>(endpoint),
    queryKey: ['/api/chat/sessions', params]
  };
}

/**
 * Create query options for getting a specific chat session
 */
export function createGetChatSessionQueryOptions(sessionId: number) {
  return {
    ...createQueryOptions<ChatSessionResponseData>(`/api/chat/sessions/${sessionId}`),
    enabled: !!sessionId
  };
}

/**
 * Create query options for getting a chat session by key
 */
export function createGetChatSessionByKeyQueryOptions(sessionKey: string) {
  return {
    ...createQueryOptions<ChatSessionResponseData>(`/api/chat/sessions/by-key/${encodeURIComponent(sessionKey)}`),
    enabled: !!sessionKey
  };
}

/**
 * Create query options for getting chat session with messages
 */
export function createGetChatSessionWithMessagesQueryOptions(sessionId: number) {
  return {
    ...createQueryOptions<ChatSessionWithMessagesResponseData>(`/api/chat/sessions/${sessionId}/with-messages`),
    enabled: !!sessionId
  };
}

/**
 * Create query options for getting chat messages
 */
export function createGetChatMessagesQueryOptions(sessionId: number, params?: Omit<GetChatMessagesRequestData, 'sessionId'>) {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());
  
  const endpoint = `/api/chat/sessions/${sessionId}/messages${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return {
    ...createQueryOptions<ChatMessagesResponseData>(endpoint),
    queryKey: [`/api/chat/sessions/${sessionId}/messages`, params],
    enabled: !!sessionId
  };
}

/**
 * Create query options for getting chat session participants
 */
export function createGetChatSessionParticipantsQueryOptions(sessionId: number) {
  return {
    ...createQueryOptions<ChatSessionParticipantsResponseData>(`/api/chat/sessions/${sessionId}/participants`),
    enabled: !!sessionId
  };
}

// Mutation Helpers for React Query

/**
 * Create mutation function for creating a chat session
 */
export const createChatSessionMutation = createMutationFn<CreateChatSessionResponseData, CreateChatSessionRequestData>('/api/chat/sessions', 'POST');

/**
 * Create mutation function for updating a chat session
 */
export function createUpdateChatSessionMutation(sessionId: number) {
  return createMutationFn<UpdateChatSessionResponseData, UpdateChatSessionRequestData>(`/api/chat/sessions/${sessionId}`, 'PUT');
}

/**
 * Create mutation function for deleting a chat session
 */
export function createDeleteChatSessionMutation(sessionId: number) {
  return createMutationFn<DeleteChatSessionResponseData, void>(`/api/chat/sessions/${sessionId}`, 'DELETE');
}

/**
 * Create mutation function for creating a chat message
 */
export const createChatMessageMutation = createMutationFn<CreateChatMessageResponseData, CreateChatMessageRequestData>('/api/chat/messages', 'POST');

/**
 * Create mutation function for updating a chat message
 */
export function createUpdateChatMessageMutation(messageId: number) {
  return createMutationFn<UpdateChatMessageResponseData, UpdateChatMessageRequestData>(`/api/chat/messages/${messageId}`, 'PUT');
}

/**
 * Create mutation function for deleting a chat message
 */
export function createDeleteChatMessageMutation(messageId: number) {
  return createMutationFn<DeleteChatMessageResponseData, void>(`/api/chat/messages/${messageId}`, 'DELETE');
}

/**
 * Generate a title for a chat session based on its conversation content
 */
export async function generateChatSessionTitle(sessionId: number): Promise<{ title: string; updated: boolean; sessionId: number }> {
  const response = await authenticatedFetch(`/api/chat/sessions/${sessionId}/generate-title`, {
    method: 'POST',
  });
  
  return handleApiResponse(response);
}
